import {useState} from 'react';
import {Box,Button,Select,MenuItem,TextField,Typography} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {styled} from '@mui/material/styles';
import Popup from './Popup.js';
import {useSnackbar} from '../SnackbarContext.js';
import {useLoading} from '../LoadingContext';
import {createAttachment} from '../../integration/ObjectManager.js';

const VisuallyHiddenInput=styled('input')({
    clip:'rect(0 0 0 0)',
    clipPath:'inset(50%)',
    height:1,
    overflow:'hidden',
    position:'absolute',
    bottom:0,
    left:0,
    whiteSpace:'nowrap',
    width:1,
});

// Generic "+" popup for a ListTab-based Attachment list (see e.g. ProductDetailAttachments.js) -
// also reused directly (not through ListTab) wherever a specific document type must be forced,
// e.g. ProductGlutenFreeReportRequiredPopup.js, so there's exactly one upload UI everywhere
// instead of a separate one-off per caller. A plain field-form popup (PageDetail-based, like
// NoteDetailPopup) doesn't fit here since the record is a file upload, not editable fields -
// this talks to the same createAttachment() upload endpoint directly instead.
//
// query (ListTab's popupComponentQuery prop, or passed directly by a non-ListTab caller) carries
// per-caller config as "key=value" strings:
//   table_name=<entity table this attachment belongs to>       (required)
//   document_type=<a Attachment_Document_Type value>            (optional - locks the type,
//                                                                 hides the picker, instead of
//                                                                 letting the user choose one)
//   accept=<comma-separated accepted mime types>                (optional, default: unrestricted)
// title/message are plain props (not query-driven) for callers that need a more specific title
// or an explanatory line above the form than the generic default.
const AttachmentUploadPopup=({popupCallBack,query=[],application,parent,userData,title,message,titleBackgroundColor})=>{
    const [selectedFile,setSelectedFile]=useState(null);
    const [description,setDescription]=useState('');
    const {showSnackbar}=useSnackbar();
    const {showLoading,hideLoading}=useLoading();

    const getQueryParam=(key)=>{
        const item=(query||[]).find((q)=>q.startsWith(key+'='));
        return item?item.slice(key.length+1):null;
    };
    const tableName=getQueryParam('table_name');
    const lockedDocumentType=getQueryParam('document_type');
    // No restriction by default - the backend's own allowlist (empty unless an admin sets
    // ATTACHMENT_ALLOWED_MIME_TYPES) is the actual source of truth. A caller can still pass
    // e.g. query={['accept=application/pdf']} to narrow the file picker itself.
    const acceptedMimeTypesParam=getQueryParam('accept');
    const acceptedMimeTypes=acceptedMimeTypesParam?acceptedMimeTypesParam.split(','):null;
    const documentTypeOptions=application?.globalData?.list_of_values?.Attachment_Document_Type||{};
    const [documentType,setDocumentType]=useState(lockedDocumentType||'');

    const handleFileChange=(event)=>{
        const file=event.target.files[0];
        event.target.value=null;
        if(!file) return;
        if(acceptedMimeTypes&&!acceptedMimeTypes.includes(file.type)){
            showSnackbar(application.getLocale("ErrorMsgFileTypeNotSupported"),'error');
            return;
        }
        setSelectedFile(file);
    };

    const handleUpload=async()=>{
        if(!selectedFile){
            showSnackbar(application.getLocale("ErrorMsgFileTypeNotSupported"),'error');
            return;
        }
        if(!documentType){
            showSnackbar(application.getLocale("ErrorMsgDocumentTypeRequired"),'error');
            return;
        }
        showLoading();
        const formData=new FormData();
        formData.append('parent_id',parent?.parent_field_value);
        formData.append('table_name',tableName||'');
        formData.append('document_type',documentType);
        if(description) formData.append('description',description);
        formData.append('file',selectedFile);

        const data=await createAttachment(formData);
        hideLoading();
        if('error' in data){
            showSnackbar(data.error,'error');
        }
        else{
            showSnackbar(application.getLocale("RecordCreatedWithSuccess"),'success');
            popupCallBack('refreshParent',{close:true});
        }
    };

    const footer=(
        <Box sx={{display:'flex',justifyContent:'flex-end',gap:1,mt:2}}>
            <Button onClick={()=>popupCallBack('closePopup',{})}>{application.getLocale("ButtonCancel")}</Button>
            <Button variant="contained" onClick={handleUpload}>{application.getLocale("ButtonUpload")}</Button>
        </Box>
    );

    return (
        <Popup
            title={title||application.getLocale("PopupTitleNewAttachment")}
            titleBackgroundColor={titleBackgroundColor}
            footerComponent={footer}
            onClose={()=>popupCallBack('closePopup',{})}>
            <Box sx={{display:'flex',flexDirection:'column',gap:2,minWidth:{sm:450}}}>
                {message&&<Typography variant="body2">{message}</Typography>}
                <Box sx={{display:'flex',flexDirection:'row',flexWrap:'wrap',gap:2}}>
                    {lockedDocumentType?(
                        <TextField
                            label={application.getLocale("FieldDocumentType")}
                            value={documentTypeOptions[lockedDocumentType]||lockedDocumentType}
                            size="small"
                            disabled
                            sx={{flex:1,minWidth:180}}/>
                    ):(
                        <Select
                            displayEmpty
                            value={documentType}
                            onChange={(e)=>setDocumentType(e.target.value)}
                            size="small"
                            sx={{flex:1,minWidth:180}}
                            renderValue={(value)=>value?documentTypeOptions[value]:application.getLocale("FieldDocumentType")}>
                            {Object.keys(documentTypeOptions).map((key)=>(
                                <MenuItem key={key} value={key}>{documentTypeOptions[key]}</MenuItem>
                            ))}
                        </Select>
                    )}
                    <TextField
                        label={application.getLocale("FieldDescription")}
                        value={description}
                        onChange={(e)=>setDescription(e.target.value)}
                        size="small"
                        sx={{flex:1,minWidth:220}}
                        inputProps={{maxLength:250}}/>
                </Box>
                <Button component="label" variant="outlined" startIcon={<FileUploadIcon/>} sx={{alignSelf:'flex-start'}}>
                    {selectedFile?selectedFile.name:application.getLocale("ButtonSelectFile")}
                    <VisuallyHiddenInput type="file" accept={acceptedMimeTypes?acceptedMimeTypes.join(','):undefined} onChange={handleFileChange}/>
                </Button>
            </Box>
        </Popup>
    );
};
export default AttachmentUploadPopup;
